import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Mail } from "lucide-react";
import AavaaLogo from "@/components/AavaaLogo";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy policy — Aavaa";
    const desc = document.querySelector('meta[name="description"]');
    if (desc)
      desc.setAttribute(
        "content",
        "How Aavaa handles your data, your child's data, and your home camera feed. Plain-English privacy policy."
      );
    return () => {
      document.title =
        "Aavaa — Your Child Development Companion | AI-powered daily summaries from your home camera";
    };
  }, []);

  const LAST_UPDATED = "April 20, 2026";
  const CONTACT_EMAIL = "privacy@aavaa.ai";

  return (
    <article
      className="min-h-screen bg-[#FAFAF7] text-[#3D4A3F]"
      data-testid="privacy-page"
    >
      {/* Header */}
      <header className="border-b border-[#E2DFD8]/50 bg-[#FAFAF7]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-[#6B7264] hover:text-[#3D4A3F] transition-colors"
            data-testid="privacy-back-home"
          >
            <ArrowLeft size={16} />
            <AavaaLogo variant="wordmark" size={30} />
          </Link>
          <Link
            to="/#waitlist"
            className="rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white px-4 py-2 font-outfit font-medium text-sm transition-colors"
            data-testid="privacy-request-demo-btn"
          >
            Request demo
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        {/* Hero */}
        <div className="flex items-center gap-2 mb-3">
          <Shield size={16} className="text-[#D4A373]" />
          <p className="font-manrope text-xs tracking-[0.15em] text-[#D4A373] uppercase">
            Privacy
          </p>
        </div>
        <h1 className="font-outfit text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05] mb-4">
          Your family's privacy is the product.
        </h1>
        <p className="font-manrope text-base sm:text-lg text-[#6B7264] leading-relaxed mb-3">
          We built Aavaa to <em>reduce</em> what strangers, advertisers, and servers see about your child — not to increase it. This page explains exactly what we do with your data, in plain English.
        </p>
        <p className="font-manrope text-xs text-[#6B7264]/80 mb-12">
          Last updated: {LAST_UPDATED}
        </p>

        {/* TL;DR card */}
        <div className="bg-white rounded-[24px] p-6 sm:p-8 border border-[#E2DFD8]/60 shadow-[0_4px_24px_-8px_rgba(47,54,48,0.05)] mb-12">
          <h2 className="font-outfit text-xl font-medium mb-4 tracking-tight flex items-center gap-2">
            <span className="w-1.5 h-6 bg-[#D4A373] rounded-full" /> The short version
          </h2>
          <ul className="space-y-2.5 font-manrope text-sm text-[#3D4A3F] leading-relaxed">
            {[
              "We only collect the data we absolutely need to reach out to you or to analyze the behaviors you asked us to track.",
              "Your child's video feed is processed by the Aavaa Hub inside your home. Only the derived daily summary — and the specific clips you choose — ever leave your home.",
              "We never sell your data. We do not use your child's data for advertising. We never will.",
              "You can delete your account, all your clips, and every summary at any time. We honour that within 30 days.",
              "We use trusted AI services (OpenAI, Google Gemini) only for analysis, and only on de-identified frame data. We do not allow them to train on your content.",
            ].map((t, i) => (
              <li key={i} className="flex gap-2.5">
                <span className="text-[#8E9B82] mt-1.5 flex-shrink-0">•</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Sections */}
        <Section title="1. Who this policy applies to">
          <p>
            This policy applies to everyone who (a) visits <strong>aavaa.ai</strong>, (b) joins our early-access waitlist, (c) participates in a private beta, or (d) uses Aavaa in your home once we launch.
          </p>
          <p>
            Aavaa is built first for parents of children with developmental differences including Down syndrome and autism. We take extra care with this community because we are members of it, and because the data involved is deeply personal.
          </p>
        </Section>

        <Section title="2. What we collect today (during waitlist)">
          <p>Right now, Aavaa is pre-launch. The only data we collect is what you voluntarily submit on the Request-a-demo form:</p>
          <List
            items={[
              "Your name (if you share it)",
              "Your email address and/or phone number",
              "Your child's age (optional)",
              "The specific insight you're looking for about your child's day",
              "Whether you already have a home camera",
              "Any question you submit for us",
              "Basic browser metadata (IP, user agent) via standard server logs, kept for 30 days for security purposes",
            ]}
          />
          <p>
            We use this strictly to contact you about your demo, to understand what parents want, and to decide who gets early access. We don't run targeted ads. We don't sell or trade this list.
          </p>
        </Section>

        <Section title="3. What we will collect at launch (full product)">
          <p>
            Once Aavaa is live, a small set of additional data will be needed to make it work. We designed this to be the <em>minimum</em> required, not the maximum possible.
          </p>
          <List
            items={[
              "Your Aavaa account (name, email, phone, password hashes)",
              "Your child's profile (name, date of birth, gender, selected conditions)",
              "The behaviors and signals you asked Aavaa to watch for",
              "Short video clips from your connected home camera — processed locally by the Aavaa Hub whenever possible",
              "AI-derived analyses of those clips (behavior tags, timestamps, observations)",
              "The daily/weekly/monthly summaries we produce",
              "Your app usage events (which clips you opened, which alerts you acted on) — used only to improve the product",
            ]}
          />
        </Section>

        <Section title="4. How video and camera data are handled">
          <p>
            This is the most important section, so we want it to be clear.
          </p>
          <h3 className="font-outfit text-base font-medium mt-5 mb-2">Local-first processing</h3>
          <p>
            The Aavaa Hub is designed to analyze your child's camera feed <strong>inside your home</strong>. Raw video does not stream continuously to our servers.
          </p>
          <h3 className="font-outfit text-base font-medium mt-5 mb-2">What leaves your home</h3>
          <p>
            Only the following leave your home: (a) the derived daily summary text, (b) the small set of short clips that were automatically selected as supporting evidence for an observation, and (c) any clip you manually choose to share with a collaborator. These are encrypted in transit and at rest.
          </p>
          <h3 className="font-outfit text-base font-medium mt-5 mb-2">What we never do</h3>
          <List
            items={[
              "We never upload your child's live stream to any server in the background.",
              "We never share your video with advertisers, insurance companies, or data brokers.",
              "We never let any AI provider train their models on your content (see Section 7).",
              "We do not use your child's face, voice, or likeness for marketing without your explicit, written consent.",
            ]}
          />
        </Section>

        <Section title="5. How we use your data">
          <List
            items={[
              "To deliver the core service — analyze clips, produce daily summaries, send alerts.",
              "To reach out to you when your demo slot is ready or your account needs attention.",
              "To improve the product — e.g. fix bugs, tune the AI, add signals parents ask for. We use aggregate, de-identified usage data for this.",
              "To meet legal obligations if we are ever required to by law (we will fight over-broad requests).",
            ]}
          />
        </Section>

        <Section title="6. What about children's data?">
          <p>
            Aavaa is a tool used <em>by parents</em> to monitor <em>their own children</em>. Children do not interact with Aavaa directly. Only the parent (or guardian) who set up the account can see their child's summaries and clips.
          </p>
          <p>
            We do not knowingly collect data from children under 13 who are using Aavaa on their own. If you believe a child has created an Aavaa account without a parent's consent, contact us at <ContactEmail email={CONTACT_EMAIL} /> and we will remove the account and all associated data.
          </p>
          <p>
            When the child ages into their teen years, parents can export all historical data and close the account. On closure, all clips and summaries are deleted within 30 days.
          </p>
        </Section>

        <Section title="7. Third-party services we rely on">
          <p>Aavaa is built on a small set of trusted infrastructure providers. We disclose each of them so there are no surprises.</p>
          <div className="overflow-x-auto -mx-2 sm:mx-0 my-4">
            <table className="w-full text-sm font-manrope">
              <thead className="bg-[#F7F2E8] text-[#6B7264] uppercase text-xs tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2 rounded-l-lg">Service</th>
                  <th className="text-left px-3 py-2">Purpose</th>
                  <th className="text-left px-3 py-2 rounded-r-lg">Data shared</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["OpenAI (GPT-5.2)", "Frame-level visual analysis", "Individual extracted frames, no identifiers"],
                  ["Google Gemini 2.5 Pro", "Behaviour & audio understanding", "Short video segments, no identifiers"],
                  ["Amazon Web Services", "Encrypted storage, compute", "Encrypted clips, summaries, account data"],
                  ["MongoDB Atlas", "Application database", "Account + summary records"],
                  ["Resend / Postmark (TBD)", "Transactional email", "Your email address only"],
                ].map((row, i) => (
                  <tr key={i} className="border-t border-[#E2DFD8]/60 align-top">
                    <td className="px-3 py-2.5 font-medium">{row[0]}</td>
                    <td className="px-3 py-2.5 text-[#6B7264]">{row[1]}</td>
                    <td className="px-3 py-2.5 text-[#6B7264]">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            We have contractual agreements with each of these providers stating that they may not use your content for model training, advertising, or any purpose other than delivering the service we requested.
          </p>
        </Section>

        <Section title="8. How long we keep your data">
          <List
            items={[
              "Clips & summaries: Kept for as long as your account is active. You can set custom retention windows (7 days / 30 days / 6 months / keep forever) in your settings at launch.",
              "Account info: Kept while your account is open. Deleted within 30 days of account closure.",
              "Waitlist info: Kept until you ask us to remove it, or for up to 24 months of inactivity, whichever comes first.",
              "Backup / disaster-recovery copies: Purged within 35 days of the primary deletion.",
            ]}
          />
        </Section>

        <Section title="9. Your rights">
          <p>Wherever you live in the world, you have the following rights with Aavaa:</p>
          <List
            items={[
              "Access — ask us for a copy of everything we hold about you and your child.",
              "Correction — fix anything that's wrong.",
              "Deletion — ask us to delete everything. We'll honour this within 30 days unless a specific law requires us to keep a minimum record.",
              "Portability — export your clips and summaries in a common format.",
              "Withdraw consent — at any time, for any reason. You won't need to explain why.",
              "Complain — lodge a complaint with your local data-protection authority if we've failed you.",
            ]}
          />
          <p>
            To exercise any of these, email us at <ContactEmail email={CONTACT_EMAIL} /> from the email on your account. We'll reply within 7 business days.
          </p>
        </Section>

        <Section title="10. Security">
          <p>
            We use industry-standard encryption in transit (TLS 1.2+) and at rest (AES-256). Passwords are hashed with bcrypt. Access to production systems is limited to a small number of engineers, logged, and audited. The Aavaa Hub ships with signed firmware and auto-updates security patches.
          </p>
          <p>
            If we ever experience a breach that affects your account, we will notify you within 72 hours, tell you what happened, and what we're doing about it.
          </p>
        </Section>

        <Section title="11. International transfers">
          <p>
            Our servers are hosted in the European Union. When data must travel outside your region (e.g. for AI processing), we use appropriate safeguards including standard contractual clauses and data-minimization.
          </p>
        </Section>

        <Section title="12. Changes to this policy">
          <p>
            If we make material changes to this policy, we'll email every active user at least 30 days before the change takes effect. You'll always have the chance to export your data and close your account before new terms apply.
          </p>
        </Section>

        <Section title="13. Contact us">
          <div className="bg-white rounded-2xl p-5 border border-[#E2DFD8]/60 flex items-start gap-3">
            <Mail size={18} className="text-[#8E9B82] mt-0.5" />
            <div>
              <p className="font-manrope text-sm text-[#3D4A3F] leading-relaxed">
                Questions about this policy, a privacy concern, or a data request? Write to us at <ContactEmail email={CONTACT_EMAIL} />. A human will read it.
              </p>
            </div>
          </div>
        </Section>
      </main>

      <footer className="border-t border-[#E2DFD8]/60 mt-12">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="privacy-footer-home">
            <AavaaLogo variant="wordmark" size={28} />
          </Link>
          <span className="font-manrope text-xs text-[#6B7264]/70">© 2026 Aavaa</span>
        </div>
      </footer>
    </article>
  );
}

/* ================= Small helpers ================= */

function Section({ title, children }) {
  return (
    <section className="mb-10" data-testid={`privacy-section-${title.split(".")[0]}`}>
      <h2 className="font-outfit text-2xl font-medium tracking-tight mb-4 flex items-start gap-3 leading-tight">
        <span className="w-1.5 h-7 bg-[#D4A373] rounded-full flex-shrink-0 mt-1" />
        <span>{title}</span>
      </h2>
      <div className="space-y-3 font-manrope text-[15px] leading-relaxed text-[#3D4A3F] pl-5 border-l border-[#E2DFD8]/50 ml-[3px]">
        {children}
      </div>
    </section>
  );
}

function List({ items }) {
  return (
    <ul className="space-y-2 pl-1">
      {items.map((t, i) => (
        <li key={i} className="flex gap-2.5">
          <span className="text-[#8E9B82] mt-1.5 flex-shrink-0">•</span>
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function ContactEmail({ email }) {
  return (
    <a
      href={`mailto:${email}`}
      className="text-[#8E9B82] font-medium underline decoration-[#8E9B82]/40 decoration-dotted underline-offset-4 hover:decoration-[#8E9B82] hover:text-[#7A8A6E] transition-colors"
    >
      {email}
    </a>
  );
}
